/**
 * Moteur de formulaire piloté par les tâches — chaque tâche (txt2img,
 * img2img, inpaint, ControlNet, édition…) déclare ses champs ; le moteur
 * les rend, gère la visibilité conditionnelle, garde les valeurs communes
 * d'un changement de tâche à l'autre et sérialise vers GenerationRequest.
 */
import { APIClient } from "../api/client.js";
import { accordion } from "../components/Accordion.js";
import { generateButton } from "../components/GenerateButton.js";
import { h } from "../components/dom.js";
import {
	type FieldHandle,
	checkboxField,
	numberField,
	seedField,
	selectField,
	sliderField,
	textAreaField,
} from "../components/fields.js";
import type { ControlnetType, GenerationRequest, ModelInfo } from "../types/index.js";
import { sourceImageUpload } from "../workspaces/image/SourceUploads.js";

export type TaskId = "txt2img" | "img2img" | "inpaint" | "controlnet" | "edit";

export interface FormState {
	taskId: TaskId;
	model: ModelInfo | null;
}

export type FieldKind =
	| "textarea"
	| "slider"
	| "select"
	| "checkbox"
	| "number"
	| "seed"
	| "source-image"
	| "mask"
	| "control-upload";

export interface TaskFieldDef {
	id: string;
	label: string;
	kind: FieldKind;
	defaultValue: unknown;
	min?: number;
	max?: number;
	step?: number;
	format?: (value: number) => string;
	options?: { value: string; label: string }[];
	placeholder?: string;
	help?: string;
	/** Champ affiché seulement pour certaines tâches/conditions. */
	visible?: (state: FormState) => boolean;
	/**
	 * Rangé sous « Réglages avancés », replié. Ce qu'il faut pour une première
	 * image tient en une description : l'audit du 03/10 montrait Sampler, CFG,
	 * Seed et Batch au même niveau que le texte, à quelqu'un qui découvre.
	 */
	advanced?: boolean;
	/**
	 * Ce que le champ dit de lui-même une fois le modèle et sa valeur connus.
	 *
	 * « Un réglage doit faire quelque chose, ou dire qu'il ne fait rien » : le
	 * curseur de guidage est sans effet à 1 ou moins partout (diffusers coupe
	 * le CFG) et sans effet à toute valeur sur FLUX.1-schnell. Un champ décrit
	 * donc lui-même quand il est mort, plutôt que de laisser la page deviner.
	 */
	state?: (context: FieldContext) => FieldState;
}

/** Ce sur quoi un champ se prononce : le modèle choisi et sa propre valeur. */
export interface FieldContext {
	form: FormState;
	value: unknown;
}

export interface FieldState {
	/** Grisé : la valeur ne peut rien changer. */
	disabled?: boolean;
	/** Une phrase sous le champ, remplacée à chaque changement. */
	note?: string | null;
}

export interface ImageTaskDef {
	id: TaskId;
	label: string;
	mode: NonNullable<GenerationRequest["mode"]>;
	/** La capacité que cette pastille demande, quand elle diffère du `mode`
	 * envoyé — l'édition Kontext s'envoie en img2img mais n'est offerte que par
	 * un modèle Kontext. Le serveur les énumère (`ModelInfo.supported_modes`). */
	requires?: string;
	fields: TaskFieldDef[];
}

interface UploadHandle {
	getRef(): string | null;
	reset(): void;
}

interface RegisteredField {
	def: TaskFieldDef;
	handle: FieldHandle<unknown> | UploadHandle;
	wrapper: HTMLElement;
	/** Créé seulement pour un champ qui se prononce sur lui-même. */
	note: HTMLElement | null;
}

export interface TaskFormOptions {
	onChange?: () => void;
	onSubmit?: () => void;
}

export class TaskForm {
	readonly element: HTMLElement;
	private tasks: ImageTaskDef[];
	private taskSelector: HTMLElement;
	private fieldsContainer: HTMLElement;
	private state: FormState;
	private registered: RegisteredField[] = [];
	private options: TaskFormOptions;
	private schedulerSelect: FieldHandle<string> | null = null;
	private schedulerArch = "";
	private beforeSubmit: HTMLElement;

	constructor(tasks: ImageTaskDef[], options: TaskFormOptions = {}) {
		this.tasks = tasks;
		this.options = options;
		this.state = { taskId: tasks[0].id, model: null };

		this.taskSelector = h("div", { class: "task-selector", role: "tablist" });
		this.fieldsContainer = h("div", { class: "task-fields" });
		const submit = generateButton("Générer", () => this.options.onSubmit?.());
		// Ce qui se décide juste avant d'appuyer -- l'estimation, la machine qui
		// fera le travail -- se lit au-dessus du bouton, pas en dessous.
		this.beforeSubmit = h("div", { class: "task-before-submit" });
		this.element = h(
			"div",
			null,
			this.taskSelector,
			this.fieldsContainer,
			this.beforeSubmit,
			submit.element
		);

		this.renderTaskSelector();
		// Silent: a caller's onChange typically reads the form back
		// (`form.getValue(...)`), and while this constructor runs, `const form
		// = new TaskForm(...)` is still in its temporal dead zone. Firing it
		// here threw a ReferenceError out of the image workspace's build() and
		// the whole shell never rendered -- a blank page from the redesign on.
		// The first render is not a change; the caller asks for what it needs
		// once it holds the form.
		this.selectTask(tasks[0].id, false);
	}

	setModel(model: ModelInfo | null): void {
		const archChanged = (this.state.model?.architecture ?? "") !== (model?.architecture ?? "");
		const modelChanged = (this.state.model?.id ?? "") !== (model?.id ?? "");
		this.state = { ...this.state, model };
		if (archChanged) void this.refreshSchedulers(model?.architecture ?? "sd15");
		if (modelChanged) this.applyModelDefaults(model);
		this.renderTaskSelector();
		this.applyVisibility();
		this.refreshFormatChooser();
	}

	/**
	 * Reprendre les réglages que ce modèle demande.
	 *
	 * Le catalogue les choisit modèle par modèle — 4 étapes et un guidage nul
	 * pour FLUX.1-schnell, 8 pour DreamShaper XL Turbo, 20 et 4,5 pour PixArt-Σ
	 * et Sana — et l'API les sert depuis toujours. La page ne les lisait pas :
	 * elle envoyait 30 et 7,5 en dur pour tout le monde, donc sept fois et demie
	 * le travail nécessaire sur un modèle distillé pour en faire quatre, et une
	 * image sur-guidée ailleurs (mesuré sur PixArt-Σ, même graine : contrastes
	 * brûlés et fourrure empâtée à 30/7,5).
	 *
	 * Ce que la personne a réglé elle-même l'emporte : on ne remplace une
	 * valeur que si elle est encore celle qu'on avait posée. Pas de drapeau à
	 * tenir — l'égalité suffit, et elle se trompe seulement quand on remet à la
	 * main la valeur qui était déjà là.
	 */
	private applyModelDefaults(model: ModelInfo | null): void {
		for (const [id, wanted] of [
			["steps", model?.default_steps],
			["guidance", model?.default_guidance_scale],
			// La résolution d'entraînement, par la même règle : ce que la
			// personne a réglé l'emporte, on ne remplace que la valeur qu'on
			// avait posée. Mesuré sur DreamShaper à graine fixe : 512 donne un
			// phare, 1024 en donne deux.
			["width", model?.default_resolution],
			["height", model?.default_resolution],
		] as const) {
			if (typeof wanted !== "number") continue;
			const field = this.registered.find((f) => f.def.id === id);
			const previous = this.appliedDefaults[id] ?? this.defaultOf(id);
			const current = field && "getValue" in field.handle ? field.handle.getValue() : undefined;
			const untouched = current === undefined || current === previous;
			this.appliedDefaults[id] = wanted;
			if (!untouched) continue;
			this.persistedValues[id] = wanted;
			if (field && "setValue" in field.handle) field.handle.setValue(wanted as never);
		}
	}

	/** La valeur que la déclaration de tâche porte, avant tout modèle. */
	private defaultOf(id: string): unknown {
		for (const task of this.tasks) {
			const def = task.fields.find((f) => f.id === id);
			if (def) return def.defaultValue;
		}
		return undefined;
	}

	/**
	 * Les sous-modes que ce modèle sait jouer.
	 *
	 * Le serveur les calcule et les envoie (`supported_modes`) ; on ne
	 * redéduit rien ici. Un serveur qui ne les envoie pas — ou aucun modèle
	 * choisi — rend tout, ce qui est le comportement d'avant : le refus reste
	 * de toute façon côté machine, et cacher une pastille par excès de zèle
	 * serait pire que de la laisser.
	 */
	private offeredTasks(): ImageTaskDef[] {
		const offered = this.state.model?.supported_modes;
		if (!offered?.length) return this.tasks;
		return this.tasks.filter((task) => offered.includes(task.requires ?? task.mode));
	}

	setTask(id: TaskId): void {
		this.selectTask(id, true);
	}

	private selectTask(id: TaskId, notify: boolean): void {
		this.state = { ...this.state, taskId: id };
		for (const button of this.taskSelector.querySelectorAll<HTMLButtonElement>(".task-pill")) {
			const active = button.dataset.task === id;
			button.classList.toggle("active", active);
			button.setAttribute("aria-selected", String(active));
		}
		this.renderFields(notify);
	}

	getTask(): TaskId {
		return this.state.taskId;
	}

	/** What the form is about, above its modes (the model it will use). */
	addAtTop(node: HTMLElement): void {
		this.element.insertBefore(node, this.taskSelector);
	}

	/** Ce qui se glisse entre les champs et le bouton (estimation, machine). */
	addBeforeSubmit(...nodes: HTMLElement[]): void {
		this.beforeSubmit.append(...nodes);
	}

	/** Fill a field from code, as if the person had typed it (kept across tasks). */
	setValue(id: string, value: unknown): void {
		this.persistedValues[id] = value;
		const handle = this.registered.find((f) => f.def.id === id)?.handle;
		if (handle && "setValue" in handle) handle.setValue(value as never);
	}

	getValue(id: string): unknown {
		const handle = this.registered.find((f) => f.def.id === id)?.handle;
		return handle && "getValue" in handle ? handle.getValue() : undefined;
	}

	/** Valeurs communes (prompt, seed, dimensions…) préservées entre tâches. */
	private persistedValues: Record<string, unknown> = {};

	/** Ce qu'on a posé pour le modèle courant, pour distinguer « pas touché »
	 * de « réglé exprès » sans tenir de drapeau. */
	private appliedDefaults: Record<string, unknown> = {};

	private renderTaskSelector(): void {
		const offered = this.offeredTasks();
		// Le mode courant peut disparaître en changeant de modèle. On le
		// remplace AVANT de redessiner : `selectTask` ne fait que basculer la
		// classe active sur les pastilles déjà en place, donc l'appeler après
		// laisserait l'ancienne liste à l'écran, avec l'ancien mode surligné.
		const dropped = !offered.some((task) => task.id === this.state.taskId);
		if (dropped) this.state = { ...this.state, taskId: offered[0].id };
		this.taskSelector.replaceChildren(
			...offered.map((task) =>
				h(
					"button",
					{
						class: "task-pill",
						type: "button",
						role: "tab",
						"data-task": task.id,
						onclick: () => this.setTask(task.id),
					},
					task.label
				)
			)
		);
		for (const button of this.taskSelector.querySelectorAll<HTMLButtonElement>(".task-pill")) {
			const active = button.dataset.task === this.state.taskId;
			button.classList.toggle("active", active);
			button.setAttribute("aria-selected", String(active));
		}
		if (dropped) this.renderFields(true);
	}

	private renderFields(notify: boolean): void {
		// Sauvegarde les valeurs courantes avant de reconstruire.
		for (const f of this.registered) {
			if ("getValue" in f.handle) this.persistedValues[f.def.id] = f.handle.getValue();
		}
		this.registered = [];
		this.fieldsContainer.replaceChildren();
		this.schedulerSelect = null;

		const task = this.tasks.find((t) => t.id === this.state.taskId) ?? this.tasks[0];
		const advanced: HTMLElement[] = [];
		for (const def of task.fields) {
			const registered = this.buildField(def);
			this.registered.push(registered);
			if (def.advanced) advanced.push(registered.wrapper);
			else this.fieldsContainer.appendChild(registered.wrapper);
		}
		if (task.fields.some((def) => def.id === "width")) {
			this.fieldsContainer.appendChild(this.buildFormatChooser());
		}
		if (advanced.length > 0) {
			const section = accordion({
				title: "Réglages avancés",
				children: advanced,
				open: this.advancedOpen,
			});
			section.element.addEventListener("click", () => {
				this.advancedOpen = section.isOpen();
			});
			this.fieldsContainer.appendChild(section.element);
		}
		this.applyVisibility();
		this.refreshFormatChooser();
		if (notify) this.options.onChange?.();
	}

	/** Gardé d'un sous-mode à l'autre : qui a ouvert les réglages les retrouve. */
	private advancedOpen = false;
	private formatButtons: HTMLButtonElement[] = [];

	/**
	 * Le format en un mot, à la place de deux curseurs en pixels. Calculé sur
	 * la résolution d'entraînement du modèle (512 pour SD 1.5, 1024 pour SDXL),
	 * à surface à peu près égale, par multiples de 64 : ce que le modèle sait
	 * dessiner sans déformer. Les curseurs restent dans les réglages avancés.
	 */
	private buildFormatChooser(): HTMLElement {
		const formats: { label: string; ratio: number }[] = [
			{ label: "Carré", ratio: 1 },
			{ label: "Portrait", ratio: 3 / 4 },
			{ label: "Paysage", ratio: 4 / 3 },
		];
		this.formatButtons = formats.map(
			(format) =>
				h(
					"button",
					{
						class: "task-pill",
						type: "button",
						"data-ratio": String(format.ratio),
						onclick: () => {
							const [width, height] = this.sizeFor(format.ratio);
							this.setValue("width", width);
							this.setValue("height", height);
							this.refreshFormatChooser();
							this.options.onChange?.();
						},
					},
					format.label
				) as HTMLButtonElement
		);
		return h(
			"div",
			{ class: "form-group" },
			h("label", { class: "field-label" }, "Format"),
			h("div", { class: "task-selector", role: "group" }, ...this.formatButtons)
		);
	}

	private sizeFor(ratio: number): [number, number] {
		const base = this.state.model?.default_resolution ?? 1024;
		const snap = (value: number) => Math.max(64, Math.round(value / 64) * 64);
		return [snap(base * Math.sqrt(ratio)), snap(base / Math.sqrt(ratio))];
	}

	private refreshFormatChooser(): void {
		const width = this.getValue("width");
		const height = this.getValue("height");
		for (const button of this.formatButtons) {
			const [w, h] = this.sizeFor(Number(button.dataset.ratio));
			button.classList.toggle("active", width === w && height === h);
		}
	}

	private buildField(def: TaskFieldDef): RegisteredField {
		const persisted = this.persistedValues[def.id];
		const initial = persisted !== undefined ? persisted : def.defaultValue;
		const onChange = () => {
			this.applyFieldStates();
			this.options.onChange?.();
		};

		let handle: FieldHandle<unknown> | UploadHandle;
		let inner: HTMLElement;

		switch (def.kind) {
			case "textarea": {
				const field = textAreaField({
					label: def.label,
					placeholder: def.placeholder,
					value: initial as string,
					maxLength: def.max,
					onInput: onChange,
				});
				handle = field;
				inner = field.element;
				break;
			}
			case "slider": {
				const field = sliderField({
					label: def.label,
					min: def.min ?? 0,
					max: def.max ?? 100,
					step: def.step,
					value: initial as number,
					format: def.format,
					onInput: onChange,
				});
				handle = field;
				inner = field.element;
				break;
			}
			case "select": {
				const field = selectField({
					label: def.label,
					options: (def.options ?? []) as { value: string; label: string }[],
					value: (initial as string) || (def.options?.[0]?.value ?? ""),
					onChange,
				});
				handle = field;
				inner = field.element;
				break;
			}
			case "checkbox": {
				const field = checkboxField({ label: def.label, checked: initial as boolean, onChange });
				handle = field;
				inner = field.element;
				break;
			}
			case "number": {
				const field = numberField({
					label: def.label,
					value: initial as number,
					min: def.min,
					max: def.max,
					step: def.step,
					onInput: onChange,
				});
				handle = field;
				inner = field.element;
				break;
			}
			case "seed": {
				const field = seedField(initial as number);
				const input = field.element.querySelector("input");
				input?.addEventListener("input", onChange);
				handle = field;
				inner = field.element;
				break;
			}
			case "source-image":
			case "mask":
			case "control-upload": {
				const upload = sourceImageUpload({
					label: def.label,
					help: def.help,
					getControlType:
						def.kind === "control-upload"
							? () => this.getValue("controlnet_type") as ControlnetType | undefined
							: undefined,
					onUploaded: onChange,
				});
				handle = upload;
				inner = upload.element;
				break;
			}
		}

		const note = def.state ? h("p", { class: "field-note hidden" }) : null;
		const wrapper = h(
			"div",
			null,
			inner,
			note,
			def.help &&
				def.kind !== "control-upload" &&
				def.kind !== "source-image" &&
				def.kind !== "mask"
				? h("p", { class: "help-text" }, def.help)
				: null
		);
		return { def, handle, wrapper, note };
	}

	private applyVisibility(): void {
		for (const f of this.registered) {
			const visible = f.def.visible ? f.def.visible(this.state) : true;
			f.wrapper.classList.toggle("hidden", !visible);
		}
		this.applyFieldStates();
	}

	/**
	 * Griser ce qui ne peut rien faire, et dire pourquoi.
	 *
	 * Rejoué au changement de modèle **et** à chaque saisie : le guidage passe
	 * dans sa zone morte en glissant le curseur, pas en changeant de modèle.
	 * Le contrôle reste utilisable quand il n'est mort qu'à sa valeur courante
	 * — sinon on ne pourrait plus en sortir.
	 */
	private applyFieldStates(): void {
		for (const f of this.registered) {
			if (!f.def.state || !f.note) continue;
			const value = "getValue" in f.handle ? f.handle.getValue() : undefined;
			const { disabled = false, note = null } = f.def.state({ form: this.state, value });
			f.note.textContent = note ?? "";
			f.note.classList.toggle("hidden", !note);
			f.wrapper.classList.toggle("field-inert", disabled);
			for (const control of f.wrapper.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
				"input, select, textarea"
			)) {
				control.disabled = disabled;
			}
		}
	}

	private async refreshSchedulers(architecture: string): Promise<void> {
		if (this.schedulerArch === architecture) return;
		this.schedulerArch = architecture;
		const target = this.registered.find((f) => f.def.id === "scheduler")?.handle;
		try {
			const schedulers = await APIClient.getSchedulers(architecture);
			// La description sert d'infobulle : ces noms ne disent rien à qui
			// n'a jamais réglé un échantillonneur, et c'est le public visé.
			const options = schedulers.map((s) => ({
				value: s.id,
				label: s.name,
				title: s.description,
			}));
			// Une architecture que le serveur ne connaît pas renvoie une liste
			// vide. La vider ici laisserait un select sans aucune option, donc
			// sans valeur, et `scheduler: ""` n'est pas une valeur que l'API
			// accepte : c'est ainsi que FLUX ne pouvait pas être généré du tout.
			// Garder la liste en place laisse une valeur valide dans la requête.
			if (options.length === 0) return;
			if (target && "setValue" in target) {
				// Reconstruction de la liste d'options du select existant.
				const select = target.element.querySelector("select");
				if (select) {
					select.replaceChildren(
						...options.map((opt) => h("option", { value: opt.value, title: opt.title }, opt.label))
					);
					const persisted = this.persistedValues.scheduler as string | undefined;
					select.value =
						persisted && options.some((o) => o.value === persisted)
							? persisted
							: (options[0]?.value ?? "");
				}
			}
		} catch {
			// Laisse la liste courante en place.
		}
	}

	/** Construit la requête API depuis les valeurs du formulaire. */
	buildRequest(): GenerationRequest {
		const task = this.tasks.find((t) => t.id === this.state.taskId) ?? this.tasks[0];
		const values: Record<string, unknown> = {};
		for (const f of this.registered) {
			if ("getValue" in f.handle) values[f.def.id] = f.handle.getValue();
		}
		const refOf = (id: string): string | null => {
			const f = this.registered.find((r) => r.def.id === id);
			return f && "getRef" in f.handle ? f.handle.getRef() : null;
		};

		const seedValue = (values.seed as number) ?? -1;
		const request: GenerationRequest = {
			mode: task.mode,
			prompt: (values.prompt as string) ?? "",
			negative_prompt: (values.negative_prompt as string) ?? "",
			width: (values.width as number) ?? 1024,
			height: (values.height as number) ?? 1024,
			num_inference_steps: (values.steps as number) ?? 30,
			guidance_scale: (values.guidance as number) ?? 7.5,
			// `||`, pas `??` : un select vidé rend "", qui est falsy sans être
			// nullish, et partait tel quel dans la requête.
			scheduler: (values.scheduler as string) || "euler_a",
			...(seedValue !== -1 ? { seed: seedValue } : {}),
			batch_size: (values.batch_size as number) ?? 1,
			enhance_prompt: (values.enhance_prompt as boolean) ?? false,
		};

		const source = refOf("init_image");
		if (source) {
			request.init_image = source;
			if (task.mode === "img2img") request.strength = (values.strength as number) ?? 0.75;
		}
		const mask = refOf("mask_image");
		if (mask) request.mask_image = mask;
		const control = refOf("control_image");
		if (control) {
			request.control_image = control;
			request.controlnet_type = (values.controlnet_type as ControlnetType) ?? "canny";
			request.controlnet_conditioning_scale = (values.controlnet_scale as number) ?? 1.0;
		}
		const ipRef = refOf("ip_adapter_image");
		if (ipRef) {
			request.ip_adapter_image = ipRef;
			request.ip_adapter_scale = (values.ip_adapter_scale as number) ?? 0.6;
		}
		return request;
	}
}
